
import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"

export interface Document {
  id: string
  title: string
  content: string
  type: string
  created_at: string
  user_id?: string
  user_name?: string
  user_city?: string
  user_state?: string
}

export function useDocuments() {
  const { toast } = useToast()
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null)
  const [editedContent, setEditedContent] = useState("")
  const [isEditing, setIsEditing] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([])
  const [showDeleteAlert, setShowDeleteAlert] = useState(false)
  const [documentsToDelete, setDocumentsToDelete] = useState<string[]>([])

  const { data: documents, isLoading, refetch } = useQuery({
    queryKey: ["documents"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("documents")
        .select("*")
        .order("created_at", { ascending: false })

      if (error) throw error
      return data
    },
  })

  const filteredDocuments = documents?.filter(doc => 
    doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.type.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleDelete = async (id: string) => {
    const { error } = await supabase
      .from("documents")
      .delete()
      .eq("id", id)

    if (error) {
      toast({
        title: "Erro ao excluir documento",
        description: error.message,
        variant: "destructive",
      })
      return
    }

    toast({
      title: "Documento excluído",
      description: "O documento foi excluído com sucesso.",
    })
    refetch()
  }

  const handleDeleteSelected = async (ids: string[]) => {
    setDocumentsToDelete(ids)
    setShowDeleteAlert(true)
  }

  const confirmDeleteSelected = async () => {
    const { error } = await supabase
      .from("documents")
      .delete()
      .in("id", documentsToDelete)

    if (error) {
      toast({
        title: "Erro ao excluir documentos",
        description: error.message,
        variant: "destructive",
      })
      return
    }

    toast({
      title: "Documentos excluídos",
      description: `${documentsToDelete.length} documento(s) foram excluídos com sucesso.`,
    })
    setSelectedDocuments([])
    setShowDeleteAlert(false)
    refetch()
  }

  const handleEdit = async () => {
    if (!selectedDocument) return

    const { error } = await supabase
      .from("documents")
      .update({ content: editedContent })
      .eq("id", selectedDocument.id)

    if (error) {
      toast({
        title: "Erro ao atualizar documento",
        description: error.message,
        variant: "destructive",
      })
      return
    }

    toast({
      title: "Documento atualizado",
      description: "O documento foi atualizado com sucesso.",
    })
    setSelectedDocument(null)
    setIsEditing(false)
    refetch()
  }

  const handleViewDocument = (document: Document) => {
    setSelectedDocument(document)
    setEditedContent(document.content)
    setIsEditing(false)
  }

  const handleSelectDocument = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedDocuments(prev => [...prev, id])
    } else {
      setSelectedDocuments(prev => prev.filter(docId => docId !== id))
    }
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked && filteredDocuments) {
      setSelectedDocuments(filteredDocuments.map(doc => doc.id))
    } else {
      setSelectedDocuments([])
    }
  }

  return {
    documents: filteredDocuments || [],
    isLoading,
    selectedDocument,
    editedContent,
    isEditing,
    searchTerm,
    selectedDocuments,
    showDeleteAlert,
    documentsToDelete,
    setSearchTerm,
    setEditedContent,
    setSelectedDocument,
    setIsEditing,
    setShowDeleteAlert,
    handleDelete,
    handleDeleteSelected,
    confirmDeleteSelected,
    handleEdit,
    handleViewDocument,
    handleSelectDocument,
    handleSelectAll,
  }
}
